//frontend/src/components/Admin/AdminUsers.jsx

import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Form, Modal, Row, Col, Alert, Pagination } from 'react-bootstrap';
import { FaEye, FaLock, FaUnlock, FaTrash, FaSearch } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage] = useState(25);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await api.get('/admin/users');
            // Handle both paginated and non-paginated responses
            const usersData = response.data.users.data || response.data.users;
            // Exclure les superadmins
            const filteredUsers = usersData.filter(u => u.role !== 'superadmin');
            setUsers(filteredUsers);
            setCurrentPage(1); // Reset to first page when loading new data
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const toggleUserStatus = async (userId) => {
        try {
            await api.put(`/admin/users/${userId}/toggle`);
            toast.success('Statut modifié avec succès');
            loadUsers();
        } catch (error) {
            console.error('Error toggling user status:', error);
            toast.error('Erreur lors de la modification');
        }
    };

    const handleDeleteUser = async () => {
        try {
            await api.delete(`/admin/users/${selectedUser.id}`);
            toast.success('Utilisateur supprimé avec succès');
            setShowDeleteModal(false);
            loadUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleShowDetails = (user) => {
        setSelectedUser(user);
        setShowDetailModal(true);
    };

    const handleShowDeleteConfirm = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    // Filter users based on search term
    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Generate pagination items
    const renderPaginationItems = () => {
        const items = [];
        
        // Previous button
        items.push(
            <Pagination.Prev 
                key="prev" 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1}
            />
        );

        // First page
        if (currentPage > 3) {
            items.push(
                <Pagination.Item key={1} onClick={() => paginate(1)}>
                    {1}
                </Pagination.Item>
            );
            if (currentPage > 4) {
                items.push(<Pagination.Ellipsis key="ellipsis1" />);
            }
        }

        // Page numbers around current page
        for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
            items.push(
                <Pagination.Item 
                    key={i} 
                    active={i === currentPage} 
                    onClick={() => paginate(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Last page
        if (currentPage < totalPages - 2) {
            if (currentPage < totalPages - 3) {
                items.push(<Pagination.Ellipsis key="ellipsis2" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => paginate(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        // Next button
        items.push(
            <Pagination.Next 
                key="next" 
                onClick={() => paginate(currentPage + 1)} 
                disabled={currentPage === totalPages}
            />
        );

        return items;
    };

    return (
        <Container className="py-4">
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h4>Gestion des Utilisateurs</h4>
                    <Badge bg="primary">{filteredUsers.length} utilisateurs</Badge>
                </Card.Header>
                <Card.Body>
                    {/* Barre de recherche */}
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <FaSearch />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        placeholder="Rechercher un utilisateur..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1); // Reset to first page on search
                                        }}
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={6} className="text-end">
                            <small className="text-muted">
                                Page {currentPage} sur {totalPages || 1} | 
                                Affichage {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} sur {filteredUsers.length}
                            </small>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary"></div>
                            <p className="mt-3">Chargement...</p>
                        </div>
                    ) : (
                        <>
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Nom d'utilisateur</th>
                                        <th>Email</th>
                                        <th>Nom complet</th>
                                        <th>Rôle</th>
                                        <th>Téléphone</th>
                                        <th>Statut</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td>{user.id}</td>
                                            <td>{user.username}</td>
                                            <td>{user.email}</td>
                                            <td>{user.first_name} {user.last_name}</td>
                                            <td>
                                                <Badge bg={user.role === 'admin' ? 'warning' : 'info'}>
                                                    {user.role === 'admin' ? 'Admin' : 'Utilisateur'}
                                                </Badge>
                                            </td>
                                            <td>{user.phone || '-'}</td>
                                            <td>
                                                <Badge bg={user.is_active ? 'success' : 'secondary'}>
                                                    {user.is_active ? 'Actif' : 'Inactif'}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => handleShowDetails(user)}
                                                        title="Voir les détails"
                                                    >
                                                        <FaEye />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant={user.is_active ? 'outline-warning' : 'outline-success'}
                                                        onClick={() => toggleUserStatus(user.id)}
                                                        title={user.is_active ? 'Bloquer' : 'Débloquer'}
                                                    >
                                                        {user.is_active ? <FaLock /> : <FaUnlock />}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleShowDeleteConfirm(user)}
                                                        title="Supprimer"
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination>
                                        {renderPaginationItems()}
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}

                    {filteredUsers.length === 0 && !loading && (
                        <Alert variant="info" className="text-center">
                            Aucun utilisateur trouvé
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            {/* Modal Détails Utilisateur */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Détails de l'utilisateur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUser && (
                        <>
                            <Row>
                                <Col md={6}>
                                    <h6 className="text-muted">Informations de base</h6>
                                    <p><strong>ID :</strong> {selectedUser.id}</p>
                                    <p><strong>Nom d'utilisateur :</strong> {selectedUser.username}</p>
                                    <p><strong>Email :</strong> {selectedUser.email}</p>
                                    <p><strong>Nom complet :</strong> {selectedUser.first_name} {selectedUser.last_name}</p>
                                    <p><strong>Téléphone :</strong> {selectedUser.phone || 'Non renseigné'}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-muted">Informations système</h6>
                                    <p>
                                        <strong>Rôle :</strong>{' '}
                                        <Badge bg={selectedUser.role === 'admin' ? 'warning' : 'info'}>
                                            {selectedUser.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                                        </Badge>
                                    </p>
                                    <p>
                                        <strong>Statut :</strong>{' '}
                                        <Badge bg={selectedUser.is_active ? 'success' : 'secondary'}>
                                            {selectedUser.is_active ? 'Actif' : 'Inactif'}
                                        </Badge>
                                    </p>
                                    <p>
                                        <strong>Date d'inscription :</strong>{' '}
                                        {new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}
                                    </p>
                                    <p>
                                        <strong>Email vérifié :</strong>{' '}
                                        <Badge bg={selectedUser.email_verified ? 'success' : 'warning'}>
                                            {selectedUser.email_verified ? 'Oui' : 'Non'}
                                        </Badge>
                                    </p>
                                </Col>
                            </Row>

                            {selectedUser.date_of_birth && (
                                <>
                                    <hr />
                                    <h6 className="text-muted">Informations personnelles</h6>
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Date de naissance :</strong> {selectedUser.date_of_birth}</p>
                                            <p><strong>Lieu de naissance :</strong> {selectedUser.place_of_birth || '-'}</p>
                                            <p><strong>Nationalité :</strong> {selectedUser.nationality || '-'}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>Ville :</strong> {selectedUser.city || '-'}</p>
                                            <p><strong>Pays :</strong> {selectedUser.country || '-'}</p>
                                            <p><strong>Profession :</strong> {selectedUser.current_occupation || '-'}</p>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            {selectedUser.passport_number && (
                                <>
                                    <hr />
                                    <h6 className="text-muted">Informations passeport</h6>
                                    <p><strong>Numéro :</strong> {selectedUser.passport_number}</p>
                                    <p><strong>Date d'expiration :</strong> {selectedUser.passport_expiry_date || '-'}</p>
                                </>
                            )}

                            <hr />
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>Complétion du profil :</strong>{' '}
                                    <Badge bg={
                                        (selectedUser?.profile_completion_percentage || 0) >= 80 ? 'success' :
                                        (selectedUser?.profile_completion_percentage || 0) >= 50 ? 'warning' : 'danger'
                                    }>
                                        {selectedUser?.profile_completion_percentage || 0}%
                                    </Badge>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Confirmation Suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        <strong>Attention !</strong> Cette action est irréversible.
                    </Alert>
                    {selectedUser && (
                        <p>
                            Êtes-vous sûr de vouloir supprimer l'utilisateur{' '}
                            <strong>{selectedUser.first_name} {selectedUser.last_name}</strong> ({selectedUser.email}) ?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDeleteUser}>
                        Supprimer définitivement
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminUsers;
